import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NotFoundComponent } from './errors/not-found.component';
import { LoginComponent } from './login/login.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
/** Application */
import { AppRoutingModule } from './app-routing.module';
import { UsersModule } from './users/users.module';
import { CustomerFeatureModule } from './ect-feature/ect-feature.module';
import { ConversionsFeatureModule } from './conversions-feature/conversions-feature.module';
import { ServiceLogModule } from './service-log/service-log.module';

import { AppComponent } from './app.component';
import { PrimaryFooterComponent } from './primary-footer/primary-footer.component';
import { PrimaryNavComponent } from './primary-nav/primary-nav.component';
import { NavbarService } from './primary-nav/primary-nav.service';
import { PrimaryAboutComponent } from './primary-about/primary-about.component';
import { PrimaryAcknowledgementsComponent } from './primary-acknowledgements/primary-acknowledgements.component';
import { PrimaryHelpComponent } from './primary-help/primary-help.component';
import { ForbiddenComponent } from './forbidden/forbidden.component';
/** Services */
import { UserService } from './users/services/user.service';
import { MessageService } from './shared/message.service';
import { ClaimsService } from './shared/claims.service';
import { UnauthorizedComponent } from './errors/unauthorized.component';
import { ModalComponent } from './modal/modal.component';
import { LicenseService } from './primary-acknowledgements/license.service';
import { ActivityService } from './shared/activity.service';
import { TimeoutWarningComponent } from './timeout-warning/timeout-warning.component';
import { AfterLoginBlankComponent } from './after-login-blank/after-login-blank.component';
import { SecurityService } from './shared/security.service';
import { environment } from '../environments/environment';
import { EnrollMfaComponent } from './modal/enroll-mfa/enroll-mfa.component';
import { LogoutComponent } from './logout/logout.component';
import { ModalService } from './shared/modal.service';
import { UserListActionsModalComponent } from './users/user-list/user-list.component';
import { PaginationService } from './users/services/pagination.service';
import { ContactUsComponent } from './contact-us/contact-us.component';
import { PermissionsRequiredComponent } from './permissions-required/permissions-required.component';
import { MyProfileComponent } from './my-profile/my-profile.component';
import { CompareValidatorDirective } from './shared/directives/compare-validator.directive';
import { ChangePasswordComponent } from './my-profile/change-password/change-password.component';
import { MyProfileService } from './my-profile/service/my-profile.service';
import { SecurityQuestionsComponent } from './my-profile/security-questions/security-questions.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { Select2Module } from './shared/Select2Module';
import { ServiceLogService } from './service-log/services/service-log.service';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withNoXsrfProtection, withXsrfConfiguration } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TreeviewI18n } from '@laijuthomas/ngx-treeview';
import { TreeViewI18nCustom } from './shared/tree-view-i18n-custom';
import { AdminToolHeadersHttpInterceptor } from './shared/interceptors/AdminToolHeadersHttpInterceptor';
import { TwoFactorComponent } from './my-profile/two-factor/two-factor.component';
import { MfaService } from "./shared/mfa.service";
import { CustomerSelectionComponent } from './customer-support/customer-selection/customer-selection.component';
import { CustomerSelectionModalDialogComponent } from './customer-support/customer-selection-modal/customer-selection-modal-dialog.component';
import { GenericErrorComponent } from './errors/generic-error/generic-error.component';
import { DisplayIfDirective } from './shared/directives/display-if.directive';
import { ErrorMessagesService } from './shared/error-messages.service';
import { AuthenticationOptions } from '@platform-idm/sdk-javascript';
import { PlatformIdmModule } from '@platform-idm/sdk-angular'
import { MaintenanceInfoBannerComponent } from './maintenance-info-banner/maintenance-info-banner.component'
import { TestComponent } from './test/test.component';
import { AlertComponent } from './shared/components/alert/alert.component';
import { SharedModule } from './shared/shared.module';
import { GlobalErrorHandler } from './services/global-error-handler';
import { UserDetailActionsModalComponent } from './users/user-detail-actions-modal/user-detail-actions-modal.component';

/*** frontend and backend different root domains are not supported. ie admin-tool.mdiachieve.com admint-tool-dotnet-service.matrixcare.me
* So for testing purposes QA will be adding mdiachieve domains to their host file so they can simulate a new app from a different domain
* Since the backend url is set in the environment, this is hardcoded do some url
* We are changing that hardcoded url to the mdiachieve one
 */
if (window.location.href.indexOf("admin-tool2") >= 0)
  environment.adminToolServiceUrl = environment.adminToolServiceUrl.replace("admin-tool-service-dotnet", "admin-tool-service-dotnet2");
if (window.location.href.indexOf("admin-tool3") >= 0)
  environment.adminToolServiceUrl = environment.adminToolServiceUrl.replace("admin-tool-service-dotnet-qa.matrixcare.me", "admin-tool-service-dotnet3-qa.mdiachieve.com");
if (window.location.href.indexOf("admin-tool4") >= 0)
  environment.adminToolServiceUrl = environment.adminToolServiceUrl.replace("admin-tool-service-dotnet-qa.matrixcare.me", "admin-tool-service-dotnet4-qa.mdiachieve.com");

const authSettings: AuthenticationOptions = {
  app: 'at',
  ssoUrl: environment.loginUrl,
  sso_serviceUrl: environment.loginServiceUrl,
  loginUrl: '/login',
  logoutUrl: '/logout',
  authPerCustomer: false,
  backendServices: [
    environment.adminToolServiceUrl, //.net
    ""

  ],
  //idleTimeoutSeconds: 10,
  refreshUrl: `/api/idm/token-refresh`,
  createSessionUrl: `/api/idm/code-exchange`,
  claimsUrl: `/api/idm/claims`,
  environmentName: environment.environmentName,
  useBuiltInTimeoutDialogBox: true,
  afterLoginRedirectTo: '/create-session'
};




@NgModule({ declarations: [
        AppComponent,
        LoginComponent,
        NotFoundComponent,
        PrimaryNavComponent,
        PrimaryFooterComponent,
        PrimaryAboutComponent,
        PrimaryHelpComponent,
        PrimaryAcknowledgementsComponent,
        UnauthorizedComponent,
        ModalComponent,
        TimeoutWarningComponent,
        AfterLoginBlankComponent,
        EnrollMfaComponent,
        LogoutComponent,
        UserDetailActionsModalComponent,
        UserListActionsModalComponent,
        ContactUsComponent,
        ForbiddenComponent,
        PermissionsRequiredComponent,
        ChangePasswordComponent,
        MyProfileComponent,
        CompareValidatorDirective,
        SecurityQuestionsComponent,
        ResetPasswordComponent,
        TwoFactorComponent,
        CustomerSelectionComponent,
        CustomerSelectionModalDialogComponent,
        GenericErrorComponent,
        DisplayIfDirective,
        MaintenanceInfoBannerComponent,
        TestComponent,
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        //Feature Modules
        UsersModule,
        ServiceLogModule,
        CustomerFeatureModule,
        ConversionsFeatureModule,
        AppRoutingModule,
        ReactiveFormsModule.withConfig({ callSetDisabledState: 'whenDisabledForLegacyCode' }), //so the disable directive when used in reactiveforms works.
        FormsModule,
        Select2Module,
        NgbModule,
        BrowserAnimationsModule,
        SharedModule,
        PlatformIdmModule.initAuth(authSettings)], providers: [
        NavbarService,
        UserService,
        ServiceLogService,
        LicenseService,
        MessageService,
        ClaimsService,
        ActivityService,
        SecurityService,
        MfaService,
        ModalService,
        PaginationService,
        MyProfileService,
        { provide: TreeviewI18n, useClass: TreeViewI18nCustom },
        { provide: HTTP_INTERCEPTORS, useClass: AdminToolHeadersHttpInterceptor, multi: true },
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
      provideHttpClient(withInterceptorsFromDi(), withNoXsrfProtection())
    ] })
export class AppModule {

  constructor(private router: Router, private route: ActivatedRoute, private messageService: MessageService) {
    this.router.events.subscribe(event => {


    });
  }


}
